import { isAxiosError, AxiosResponse } from 'axios';
import telemetryService from '|/app/services/telemetry/telemetry-service';
import axiosInstance from '|/helpers/axios';
import { TimeAvailability, ScheduleTransferPayload } from './purchase-manager-api.types';
import getElapsedTimeInSeconds from '../utils/getElapsedTimeInSeconds';

const TIME_SLOT_SELECTION_ERROR = 'This appointment time is no longer available.';

const baseUrl = `${process.env.API_URL}/api`;

export const TIMESLOTS_ENDPOINT = `${baseUrl}/v1/scheduling/timeslots`;
export const SCHEDULE_TRANSFER_ENDPOINT = `${baseUrl}/v1/scheduling/scheduletransfer`;

export const getTransferScheduleTimeslots = (
  abortSignal?: AbortSignal
): Promise<TimeAvailability> => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-manager-api:getTransferScheduleTimeslots:start'
  });

  return axiosInstance
    .get<TimeAvailability, AxiosResponse<TimeAvailability>>(TIMESLOTS_ENDPOINT, {
      signal: abortSignal
    })
    .then(res => {
      telemetryService.trackEvent({
        name: 'purchase-manager-api:getTransferScheduleTimeslots:success',
        context: {
          elapsedTimeInSeconds: getElapsedTimeInSeconds(startTime),
          response: res.data
        }
      });

      return res.data;
    })
    .catch((err: unknown) => {
      telemetryService.trackError({
        error: err,
        name: 'purchase-manager-api:getTransferScheduleTimeslots:error',
        context: {
          elapsedTimeInSeconds: getElapsedTimeInSeconds(startTime),
          ...(isAxiosError(err) && err.response && { response: err.response.data })
        }
      });

      return Promise.reject(
        err instanceof Error ? err : new Error('Failed to get transfer schedule timeslots.')
      );
    });
};

export const submitSelectedTimeSlot = (
  payload: ScheduleTransferPayload,
  abortSignal?: AbortSignal
): Promise<void> => {
  const startTime = performance.now();

  telemetryService.trackEvent({
    name: 'purchase-manager-api:submitSelectedTimeSlot:start',
    context: {
      hasPayload: !!payload,
      request: payload
    }
  });

  return axiosInstance
    .post<unknown, AxiosResponse<unknown>>(
      SCHEDULE_TRANSFER_ENDPOINT,
      { ...payload },
      { signal: abortSignal }
    )
    .then(_res => {
      telemetryService.trackEvent({
        name: 'purchase-manager-api:submitSelectedTimeSlot:success',
        context: {
          elapsedTimeInSeconds: getElapsedTimeInSeconds(startTime),
          hasPayload: !!payload
        }
      });

      return;
    })
    .catch((err: unknown) => {
      telemetryService.trackError({
        error: err,
        name: 'purchase-manager-api:submitSelectedTimeSlot:error',
        context: {
          elapsedTimeInSeconds: getElapsedTimeInSeconds(startTime),
          hasPayload: !!payload,
          ...(isAxiosError(err) && err.response && { response: err.response.data })
        }
      });

      if (isAxiosError(err) && err.response?.status === 400) {
        throw new Error(TIME_SLOT_SELECTION_ERROR, { cause: err });
      }

      throw new Error('Failed to submit selected appointment slot.', { cause: err });
    });
};
